/**
 * 🔊 BattleAudio — Placeholder audio wiring for combat.
 *
 * Owner-supplied tracks (Feb 2026):
 *   • normal battle bopper  → /audio/battle-loop.mp4 (looping bg music while a fight is live)
 *   • Dragonious Flaire     → /audio/victory-fanfare.mp4 (one-shot on wins)
 *
 * The component mounts hidden <audio> elements once at app root and listens
 * on `window` for a small event API so ANY combat surface can trigger the
 * audio without prop-drilling:
 *
 *   window.dispatchEvent(new Event("battle-audio:start"));     // begin loop
 *   window.dispatchEvent(new Event("battle-audio:stop"));      // silence
 *   window.dispatchEvent(new Event("battle-audio:victory"));   // fanfare + stop loop
 *   window.dispatchEvent(new Event("battle-audio:defeat"));    // stop loop (no fanfare)
 *
 * Mute preference persists via `localStorage.battleAudioMuted`.
 * A tiny floating speaker toggle appears only when audio is active.
 *
 * Browsers block autoplay until the user interacts with the page. The
 * component tolerates play() rejections silently — the first user click
 * anywhere else in the app unblocks it going forward.
 */
import { useEffect, useRef, useState } from "react";

const LS_KEY = "battleAudioMuted";
const LOOP_URL = "/audio/battle-loop.mp4";
const BOSS_LOOP_URL = "/audio/world-boss-loop.mp4";  // Wild/World bosses ONLY (owner spec)
const VICTORY_URL = "/audio/victory-fanfare.mp4";
const FADE_MS = 400;   // 🎚️ Owner spec Feb 2026: 400ms crossfade

/**
 * Small RAF-based volume tween. Returns a cancel function so overlapping
 * fades don't fight each other (the newer fade cancels the older one).
 */
function _fadeVolume(el, fromVol, toVol, durationMs) {
  if (!el) return () => {};
  const start = performance.now();
  let cancelled = false;
  const step = (now) => {
    if (cancelled) return;
    const t = Math.min(1, (now - start) / Math.max(1, durationMs));
    try { el.volume = fromVol + (toVol - fromVol) * t; } catch { /* noop */ }
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
  return () => { cancelled = true; };
}

export default function BattleAudio() {
  const loopRef = useRef(null);
  const bossLoopRef = useRef(null);
  const victoryRef = useRef(null);
  const loopFadeCancel = useRef(null);
  const bossLoopFadeCancel = useRef(null);
  const victoryFadeCancel = useRef(null);
  // Which loop is currently owning the "playing" state.
  const activeLoopKind = useRef(null); // null | "normal" | "boss"
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; }
  });
  const [active, setActive] = useState(false);

  // Persist mute pref + apply to elements.
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, muted ? "1" : "0"); } catch { /* noop */ }
    if (loopRef.current) loopRef.current.muted = muted;
    if (bossLoopRef.current) bossLoopRef.current.muted = muted;
    if (victoryRef.current) victoryRef.current.muted = muted;
  }, [muted]);

  // Event listeners
  useEffect(() => {
    const cancelFades = () => {
      if (loopFadeCancel.current) { loopFadeCancel.current(); loopFadeCancel.current = null; }
      if (bossLoopFadeCancel.current) { bossLoopFadeCancel.current(); bossLoopFadeCancel.current = null; }
      if (victoryFadeCancel.current) { victoryFadeCancel.current(); victoryFadeCancel.current = null; }
    };

    // 🎚️ Fade the OTHER loop out immediately (used when switching kinds).
    const silenceOther = (keep) => {
      const other = keep === "normal" ? bossLoopRef.current : loopRef.current;
      if (!other) return;
      try {
        const from = (typeof other.volume === "number") ? other.volume : 1;
        _fadeVolume(other, from, 0, FADE_MS);
        setTimeout(() => {
          try { other.pause(); other.currentTime = 0; other.volume = 1; } catch {}
        }, FADE_MS + 20);
      } catch { /* noop */ }
    };

    const startKind = (kind) => {
      setActive(true);
      activeLoopKind.current = kind;
      const el = (kind === "boss") ? bossLoopRef.current : loopRef.current;
      const fadeSlot = (kind === "boss") ? bossLoopFadeCancel : loopFadeCancel;
      if (!el) return;
      cancelFades();
      silenceOther(kind);
      try {
        el.currentTime = 0;
        el.volume = 0;
        el.play().catch(() => { /* autoplay blocked; ignore */ });
        fadeSlot.current = _fadeVolume(el, 0, 1, FADE_MS);
      } catch { /* noop */ }
    };

    const start     = () => startKind("normal");
    const startBoss = () => startKind("boss");

    const stop = () => {
      setActive(false);
      const kind = activeLoopKind.current;
      activeLoopKind.current = null;
      const el = (kind === "boss") ? bossLoopRef.current : loopRef.current;
      const fadeSlot = (kind === "boss") ? bossLoopFadeCancel : loopFadeCancel;
      if (!el) return;
      cancelFades();
      const startVol = (typeof el.volume === "number") ? el.volume : 1;
      fadeSlot.current = _fadeVolume(el, startVol, 0, FADE_MS);
      setTimeout(() => {
        try { el.pause(); el.currentTime = 0; el.volume = 1; } catch { /* noop */ }
      }, FADE_MS + 20);
    };

    const victory = () => {
      cancelFades();
      const kind = activeLoopKind.current;
      const loop = (kind === "boss") ? bossLoopRef.current : loopRef.current;
      const fadeSlot = (kind === "boss") ? bossLoopFadeCancel : loopFadeCancel;
      const fan = victoryRef.current;
      const loopStartVol = (loop && typeof loop.volume === "number") ? loop.volume : 1;
      if (loop) fadeSlot.current = _fadeVolume(loop, loopStartVol, 0, FADE_MS);
      if (fan) {
        try {
          fan.currentTime = 0;
          fan.volume = 0;
          fan.play().catch(() => { /* noop */ });
          victoryFadeCancel.current = _fadeVolume(fan, 0, 1, FADE_MS);
        } catch { /* noop */ }
      }
      setTimeout(() => {
        try { if (loop) { loop.pause(); loop.currentTime = 0; loop.volume = 1; } }
        catch { /* noop */ }
        activeLoopKind.current = null;
        setActive(false);
      }, FADE_MS + 20);
    };

    const defeat = () => { stop(); };

    window.addEventListener("battle-audio:start", start);
    window.addEventListener("battle-audio:start-boss", startBoss);
    window.addEventListener("battle-audio:stop", stop);
    window.addEventListener("battle-audio:victory", victory);
    window.addEventListener("battle-audio:defeat", defeat);
    return () => {
      window.removeEventListener("battle-audio:start", start);
      window.removeEventListener("battle-audio:start-boss", startBoss);
      window.removeEventListener("battle-audio:stop", stop);
      window.removeEventListener("battle-audio:victory", victory);
      window.removeEventListener("battle-audio:defeat", defeat);
      cancelFades();
    };
  }, []);

  return (
    <>
      {/* Hidden audio players — MP4 audio tracks are honored by <audio>. */}
      <audio ref={loopRef} src={LOOP_URL} loop preload="auto" data-testid="battle-audio-loop" />
      <audio ref={bossLoopRef} src={BOSS_LOOP_URL} loop preload="auto" data-testid="battle-audio-boss-loop" />
      <audio ref={victoryRef} src={VICTORY_URL} preload="auto" data-testid="battle-audio-victory" />
      {/* 🎵 Persistent music toggle — visible EVERYWHERE the app is open so
          players can pre-mute the battle loop before a fight starts. Glows
          amber while audio is actively playing. */}
      <button
        type="button"
        onClick={() => setMuted((m) => !m)}
        data-testid="battle-audio-mute-toggle"
        aria-label={muted ? "Unmute battle music" : "Mute battle music"}
        title={muted ? "Unmute battle music" : "Mute battle music"}
        className={`fixed bottom-24 right-3 z-[80] px-2.5 h-10 rounded-full border flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest shadow-lg backdrop-blur-sm transition
          ${muted
            ? "bg-black/70 border-neutral-600 text-neutral-400 hover:text-neutral-200 hover:border-neutral-400"
            : active
              ? "bg-black/80 border-amber-400 text-amber-200 ring-2 ring-amber-400/40"
              : "bg-black/60 border-amber-700/60 text-amber-300 hover:border-amber-400 hover:text-amber-200"}`}
      >
        <span className="text-base leading-none">{muted ? "🔇" : "🔊"}</span>
        <span className="text-[9px]">{muted ? "Muted" : (active ? "Playing" : "Music")}</span>
      </button>
    </>
  );
}
